import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './Header.css';

const navSections = ['services', 'why-choose-us', 'case-studies', 'contact'];
const navLabels = { services: 'Services', 'why-choose-us': 'Why Us', 'case-studies': 'Projects', contact: 'Contact' };
const SECTION_ACTIVATION_OFFSET = 150;

const navItemVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.06, duration: 0.4, ease: 'easeOut' },
  }),
};

const mobileMenuVariants = {
  closed: { opacity: 0, y: '-100%', transition: { duration: 0.25 } },
  open: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const updateActiveSection = useCallback(() => {
    const scrollPosition = window.scrollY + SECTION_ACTIVATION_OFFSET;
    let current = '';
    for (const sectionId of navSections) {
      const element = document.getElementById(sectionId);
      if (element && element.offsetTop <= scrollPosition) {
        current = sectionId;
      }
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      updateActiveSection();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateActiveSection]);

  return (
    <motion.header
      className={`dark-header ${scrolled ? 'header-scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <motion.div
        className="header-logo"
        onClick={() => scrollToSection('hero')}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
      >
        <img src="/vyznit_logo.png" alt="VYZN Info Tech" className="logo-image" />
      </motion.div>

      {/* Desktop nav */}
      <nav className="dark-nav desktop-nav">
        {navSections.map((id, i) => (
          <motion.a
            key={id}
            onClick={() => scrollToSection(id)}
            className={`dark-nav-link ${activeSection === id ? 'active' : ''}`}
            custom={i}
            variants={navItemVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            {navLabels[id]}
          </motion.a>
        ))}
        <motion.button
          className="btn-primary"
          onClick={() => scrollToSection('contact')}
          custom={navSections.length}
          variants={navItemVariants}
          initial="hidden"
          animate="visible"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
        >
          Get Started
        </motion.button>
      </nav>

      {/* Mobile toggle */}
      <motion.button
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        whileTap={{ scale: 0.9 }}
        animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
        transition={{ duration: 0.25 }}
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </motion.button>

      {/* Mobile nav */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.nav
            className="dark-nav mobile-nav"
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {navSections.map((id) => (
              <a
                key={id}
                onClick={() => scrollToSection(id)}
                className={`dark-nav-link ${activeSection === id ? 'active' : ''}`}
              >
                {navLabels[id]}
              </a>
            ))}
            <button className="btn-primary" onClick={() => scrollToSection('contact')}>
              Get Started
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;